import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Checkbox, Modal, Spin, Table, Tag, Typography } from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  DownloadOutlined,
  FileExcelOutlined,
  UploadOutlined,
  WarningFilled,
} from "@ant-design/icons";
import { IMPORT_COLUMNS } from "../../../../utils/bdfExcelImport";

const { Text } = Typography;

const formatNumber = (value) =>
  value === null || value === undefined ? "-" : Number(value).toLocaleString("en-IN");

const FORMAT_COLUMNS = [
  { key: "codeNo", name: "Code No", required: true, example: "HK-077", note: "Inventory code. Must already exist in the system." },
  { key: "startDate", name: "Start Date", required: true, example: "01-11-2026", note: "DD-MM-YYYY (Excel dates also work)." },
  { key: "endDate", name: "End Date", required: true, example: "30-11-2026", note: "On or after the start date." },
  { key: "printingCharge", name: "Printing Charge", required: false, example: "1500", note: "Blank = standard printing charge for the size." },
  { key: "mountingCharge", name: "Mounting Charge", required: false, example: "1500", note: "Blank = standard mounting charge for the size." },
  { key: "specialRatePerMonth", name: "Special Rate Per Month", required: false, example: "30000", note: "Blank = the inventory's card rate (RPM)." },
];

const SECOND_SAMPLE = {
  codeNo: "HK-078",
  startDate: "05-11-2026",
  endDate: "04-12-2026",
  printingCharge: "",
  mountingCharge: "",
  specialRatePerMonth: "",
};

const StatBox = ({ label, value, color, icon }) => (
  <div
    style={{
      flex: 1,
      minWidth: 140,
      padding: "10px 14px",
      border: "1px solid #f0f0f0",
      borderLeft: `4px solid ${color}`,
      borderRadius: 6,
      background: "#fff",
    }}
  >
    <div style={{ color: "#8c8c8c", fontSize: 12 }}>
      {icon} {label}
    </div>
    <div style={{ fontSize: 22, fontWeight: 600, color }}>{value}</div>
  </div>
);

const BdfExcelImportModal = ({
  open,
  status,
  fileName,
  error,
  foundHeaders,
  info,
  plan,
  packageRateActive,
  importing,
  onImport,
  onCancel,
  onDownloadTemplate,
  onChooseAnother,
  columnKeys, // optional: only these columns are expected in the file
}) => {
  const formatColumns = columnKeys
    ? FORMAT_COLUMNS.filter((c) => columnKeys.includes(c.key))
    : FORMAT_COLUMNS;
  const chargesShown = !columnKeys || columnKeys.includes("specialRatePerMonth");
  const [onlyProblems, setOnlyProblems] = useState(false);

  useEffect(() => {
    if (open) setOnlyProblems(false);
  }, [open, fileName]);

  const planRows = plan?.rows;
  const visibleRows = useMemo(() => {
    const all = planRows || [];
    return onlyProblems ? all.filter((r) => r.status === "error") : all;
  }, [planRows, onlyProblems]);

  const defaultTag = (row, key) =>
    row.defaults && row.defaults[key] ? (
      <Text type="secondary" style={{ fontSize: 11 }}>
        {" "}
        (default)
      </Text>
    ) : null;

  const allColumns = [
    { title: "Row", dataIndex: "rowNumber", key: "rowNumber", width: 60, align: "center" },
    { title: "Code No", dataIndex: "code", key: "code", width: 120 },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      render: (v) => v || "-",
    },
    {
      title: "Printing",
      key: "printingCharge",
      width: 90,
      align: "right",
      render: (_, r) => (
        <span>
          {formatNumber(r.printingCharge)}
          {defaultTag(r, "printingCharge")}
        </span>
      ),
    },
    {
      title: "Mounting",
      key: "mountingCharge",
      width: 90,
      align: "right",
      render: (_, r) => (
        <span>
          {formatNumber(r.mountingCharge)}
          {defaultTag(r, "mountingCharge")}
        </span>
      ),
    },
    {
      title: "Special Rate",
      key: "specialRatePerMonth",
      width: 120,
      align: "right",
      render: (_, r) => (
        <span>
          {formatNumber(r.specialRatePerMonth)}
          {defaultTag(r, "specialRatePerMonth")}
        </span>
      ),
    },
    {
      title: "Start",
      dataIndex: "startDate",
      key: "startDate",
      width: 120,
      render: (v) => <span style={{ whiteSpace: "nowrap" }}>{v || "-"}</span>,
    },
    {
      title: "End",
      dataIndex: "endDate",
      key: "endDate",
      width: 120,
      render: (v) => <span style={{ whiteSpace: "nowrap" }}>{v || "-"}</span>,
    },
    {
      title: "Amount (In A/C)",
      key: "amount",
      width: 140,
      align: "right",
      render: (_, r) =>
        r.amount === null && r.status === "ready" ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            after dates
          </Text>
        ) : (
          formatNumber(r.amount)
        ),
    },
    {
      title: "Status",
      key: "status",
      render: (_, r) =>
        r.status === "ready" ? (
          <div>
            <Tag color="green" icon={<CheckCircleFilled />}>
              Ready
            </Tag>
            {r.warnings.map((w) => (
              <div key={w} style={{ color: "#d48806", fontSize: 12 }}>
                <WarningFilled /> {w}
              </div>
            ))}
          </div>
        ) : (
          <div>
            <Tag color="red" icon={<CloseCircleFilled />}>
              Problem
            </Tag>
            {r.errors.map((e) => (
              <div key={e} style={{ color: "#cf1322", fontSize: 12 }}>
                {e}
              </div>
            ))}
          </div>
        ),
    },
  ];

  const chargeKeys = ["printingCharge", "mountingCharge", "specialRatePerMonth"];
  // Without charge columns (premium block) there are no amounts to show either.
  const columns = chargesShown
    ? allColumns
    : allColumns.filter((c) => !chargeKeys.includes(c.key) && c.key !== "amount");

  const readyCount = plan?.readyCount || 0;
  const errorCount = plan?.errorCount || 0;

  const footer = [
    <Button key="template" icon={<DownloadOutlined />} onClick={onDownloadTemplate} style={{ float: "left" }}>
      Download template
    </Button>,
    status !== "loading" && status !== "intro" && (
      <Button key="another" icon={<UploadOutlined />} onClick={onChooseAnother} disabled={importing}>
        Choose another file
      </Button>
    ),
    <Button key="cancel" onClick={onCancel} disabled={importing}>
      Cancel
    </Button>,
    status === "intro" && (
      <Button key="choose" type="primary" icon={<UploadOutlined />} onClick={onChooseAnother}>
        Choose Excel file
      </Button>
    ),
    status === "preview" && (
      <Button
        key="import"
        type="primary"
        loading={importing}
        disabled={readyCount === 0 || importing}
        onClick={onImport}
      >
        {errorCount > 0
          ? `Import ${readyCount} valid row${readyCount === 1 ? "" : "s"} (skip ${errorCount})`
          : `Import ${readyCount} inventor${readyCount === 1 ? "y" : "ies"}`}
      </Button>
    ),
  ].filter(Boolean);

  return (
    <Modal
      title="Import inventories from Excel"
      open={open}
      onCancel={importing ? undefined : onCancel}
      closable={!importing}
      maskClosable={false}
      keyboard={!importing}
      width="min(1400px, 96vw)"
      footer={footer}
      destroyOnClose
    >
      {fileName && (
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">File: </Text>
          <Text strong>{fileName}</Text>
          {info?.sheetName && <Text type="secondary"> (sheet "{info.sheetName}")</Text>}
        </div>
      )}

      {status === "intro" && (
        <div>
          <Text type="secondary">
            Upload a .xlsx, .xls or .csv file (max 5 MB, up to 1000 rows). Column order and exact heading
            wording do not matter — we match them for you.{" "}
            {chargesShown ? "Amount (In A/C) is calculated automatically from the dates." : null}
          </Text>

          <Table
            size="small"
            style={{ marginTop: 12 }}
            rowKey="name"
            pagination={false}
            dataSource={formatColumns}
            columns={[
              {
                title: "Column",
                dataIndex: "name",
                width: 210,
                render: (v, r) => (
                  <span>
                    <FileExcelOutlined style={{ color: "#389e0d", marginRight: 6 }} />
                    <Text strong>{v}</Text>
                    {r.required && <Text type="danger"> *</Text>}
                  </span>
                ),
              },
              { title: "Example", dataIndex: "example", width: 120 },
              { title: "Notes", dataIndex: "note" },
            ]}
          />
          <div style={{ margin: "6px 0 14px" }}>
            <Text type="danger">*</Text>{" "}
            <Text type="secondary">required — every other column is optional.</Text>
          </div>

          <Text strong>What your sheet should look like</Text>
          <div style={{ overflowX: "auto", marginTop: 6 }}>
            <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%" }}>
              <thead>
                <tr>
                  {formatColumns.map((c) => (
                    <th
                      key={c.name}
                      style={{
                        background: "#e6f4ea",
                        border: "1px solid #b7dfc2",
                        padding: "4px 8px",
                        textAlign: "left",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {c.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[0, 1].map((i) => (
                  <tr key={i}>
                    {formatColumns.map((c) => (
                      <td
                        key={c.name}
                        style={{ border: "1px solid #e8e8e8", padding: "4px 8px", whiteSpace: "nowrap" }}
                      >
                        {i === 0 ? c.example : SECOND_SAMPLE[c.key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul style={{ margin: "14px 0 0", paddingLeft: 18, color: "#595959", fontSize: 13 }}>
            <li>Extra columns (location, size, etc.) are ignored.</li>
            <li>Rows with a problem are listed in a preview — you can import the valid ones and skip the rest.</li>
            <li>Not sure? Use "Download template" for a ready-made file.</li>
          </ul>
        </div>
      )}

      {status === "loading" && (
        <div style={{ textAlign: "center", padding: "56px 0" }}>
          <Spin size="large" />
          <div style={{ marginTop: 16, fontSize: 15, fontWeight: 500 }}>Reading your file...</div>
          <div style={{ marginTop: 4, color: "#8c8c8c", fontSize: 13 }}>
            {chargesShown
              ? "Matching Code Nos with inventories and calculating amounts. This can take a few seconds for large files."
              : "Matching Code Nos with inventories. This can take a few seconds for large files."}
          </div>
        </div>
      )}

      {status === "error" && (
        <div>
          <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />
          {foundHeaders && foundHeaders.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary">Columns found: </Text>
              {foundHeaders.map((h) => (
                <Tag key={h}>{h}</Tag>
              ))}
            </div>
          )}
          <div>
            <Text strong>Expected columns </Text>
            <Text type="secondary">(any order; * required)</Text>
            <div style={{ marginTop: 6 }}>
              {IMPORT_COLUMNS.filter((c) => !columnKeys || columnKeys.includes(c.key)).map((c) => (
                <Tag key={c.key} color={c.required ? "blue" : "default"}>
                  {c.label}
                  {c.required ? " *" : ""}
                </Tag>
              ))}
            </div>
          </div>
        </div>
      )}

      {status === "preview" && (
        <div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
            <StatBox label="Total rows" value={readyCount + errorCount} color="#595959" />
            <StatBox label="Ready to import" value={readyCount} color="#389e0d" icon={<CheckCircleFilled />} />
            <StatBox
              label="Problems (will be skipped)"
              value={errorCount}
              color={errorCount > 0 ? "#cf1322" : "#8c8c8c"}
              icon={errorCount > 0 ? <CloseCircleFilled /> : null}
            />
          </div>
          {errorCount > 0 && (
            <div style={{ marginBottom: 10, color: "#595959", fontSize: 13 }}>
              Fix the file and upload it again to include the skipped rows, or import the valid rows now.
            </div>
          )}

          {packageRateActive && chargesShown && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="A package rate is set on this BDF, so Printing, Mounting and Special Rate in the file are ignored and the amounts are spread from the package rate."
            />
          )}

          {info?.matched?.length > 0 && (
            <div
              style={{
                marginBottom: 12,
                padding: "10px 12px",
                border: "1px solid #f0f0f0",
                borderRadius: 6,
                background: "#fafafa",
              }}
            >
              <div style={{ marginBottom: 6 }}>
                <Text strong style={{ fontSize: 13 }}>
                  Columns used
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {" "}
                  (your heading → what we read it as)
                </Text>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 0" }}>
                {info.matched.map((m) => (
                  <Tag key={m.key} color="blue" style={{ marginRight: 8 }}>
                    {m.header === m.label ? (
                      m.label
                    ) : (
                      <span>
                        {m.header} → <b>{m.label}</b>
                      </span>
                    )}
                  </Tag>
                ))}
              </div>
              {info.ignored?.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Ignored columns:{" "}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {info.ignored.map((i) => i.header).join(", ")}
                  </Text>
                </div>
              )}
            </div>
          )}

          {(info?.warnings || []).map((w) => (
            <Alert key={w} type="warning" showIcon message={w} style={{ marginBottom: 8 }} />
          ))}

          {errorCount > 0 && (
            <Checkbox
              checked={onlyProblems}
              onChange={(e) => setOnlyProblems(e.target.checked)}
              style={{ marginBottom: 8 }}
            >
              Show only rows with problems
            </Checkbox>
          )}

          <Table
            size="small"
            rowKey="rowNumber"
            columns={columns}
            onRow={(r) => (r.status === "error" ? { style: { background: "#fff1f0" } } : {})}
            dataSource={visibleRows}
            // All rows on one page; the table scrolls inside the dialog (header stays put).
            pagination={false}
            scroll={{ y: "max(240px, calc(100vh - 480px))" }}
          />
        </div>
      )}
    </Modal>
  );
};

export default BdfExcelImportModal;
